import { useEffect, useRef, useState } from 'react';

/**
 * Chapter 004 · COLORWAYS
 * ────────────────────────────────────────────
 * Interactions:
 *  • Cursor-driven 3D tilt + sheen on the tall product image
 *  • Click anywhere on the image → mint ripple bursts from that point
 *    and cycles to the next finish
 *  • Keyboard ← / → switches finishes when the section is in view
 *  • Auto-advance every 6s, pauses on hover
 *  • Section ambient glow tints toward the active finish
 */

type Colorway = {
  id: string;
  code: string;
  name: string;
  subtitle: string;
  hex: string;
  glow: string;
  finish: string;
  edition: string;
  image: string;
};

const COLORWAYS: Colorway[] = [
  {
    id: 'onyx',
    code: '01 / 03',
    name: 'Reference Onyx',
    subtitle: 'The original.',
    hex: '#1A1D24',
    glow: 'rgba(26, 29, 36, 0.55)',
    finish: 'Anodized titanium',
    edition: 'Open series',
    image:
      'https://readdy.ai/api/search-image?query=Ultra%20photorealistic%20hyperreal%20studio%20product%20photograph%20of%20a%20premium%20matte%20black%20anodized%20titanium%20portable%20audio%20device%20standing%20vertically%20on%20a%20deep%20navy%20blue%20charcoal%20seamless%20studio%20background%2C%20cool%20moody%20editorial%20lighting%20with%20a%20single%20soft%20key%20light%20from%20upper%20left%20creating%20gentle%20rim%20highlights%20on%20the%20metal%20edges%2C%20subtle%20cyan%20blue%20fill%20light%2C%20shot%20on%20Hasselblad%20medium%20format%20with%2080mm%20lens%20at%20f4%20ISO%20100%2C%20visible%20microtexture%20of%20brushed%20titanium%20grain%20and%20fine%20dust%20particles%20catching%20light%2C%20accurate%20soft%20contact%20shadow%2C%20shallow%20depth%20of%20field%2C%20cinematic%20dark%20mood%2C%20lifelike%20realism%2C%20centered%20subject%2C%20luxury%20audio%20product%20photography&width=900&height=1200&seq=axiom-colorway-onyx-v5-dark&orientation=portrait',
  },
  {
    id: 'slate',
    code: '02 / 03',
    name: 'Slate Green',
    subtitle: 'For quiet interiors.',
    hex: '#5A8A78',
    glow: 'rgba(90, 138, 120, 0.28)',
    finish: 'PVD slate green',
    edition: 'Limited · 400',
    image:
      'https://readdy.ai/api/search-image?query=Ultra%20photorealistic%20hyperreal%20studio%20product%20photograph%20of%20a%20premium%20muted%20sage%20slate%20green%20PVD%20coated%20titanium%20portable%20audio%20device%20standing%20vertically%20on%20a%20deep%20navy%20blue%20charcoal%20seamless%20studio%20background%2C%20cool%20moody%20editorial%20lighting%20with%20a%20single%20soft%20key%20light%20from%20upper%20left%20creating%20gentle%20rim%20highlights%20on%20the%20matte%20metal%20surface%2C%20subtle%20cyan%20blue%20fill%20light%2C%20shot%20on%20Hasselblad%20medium%20format%20with%2080mm%20lens%20at%20f4%20ISO%20100%2C%20visible%20microtexture%20of%20soft%20matte%20PVD%20finish%20and%20fine%20dust%20particles%20catching%20light%2C%20accurate%20soft%20contact%20shadow%2C%20shallow%20depth%20of%20field%2C%20cinematic%20dark%20mood%2C%20lifelike%20realism%2C%20centered%20subject%2C%20luxury%20audio%20product%20photography&width=900&height=1200&seq=axiom-colorway-slate-v5-dark&orientation=portrait',
  },
  {
    id: 'sand',
    code: '03 / 03',
    name: 'Warm Sand',
    subtitle: 'For daylight travel.',
    hex: '#C3A97E',
    glow: 'rgba(195, 169, 126, 0.28)',
    finish: 'Bead-blasted aluminum',
    edition: 'Limited · 200',
    image:
      'https://readdy.ai/api/search-image?query=Ultra%20photorealistic%20hyperreal%20studio%20product%20photograph%20of%20a%20premium%20warm%20sand%20beige%20bead%20blasted%20anodized%20aluminum%20portable%20audio%20device%20standing%20vertically%20on%20a%20deep%20navy%20blue%20charcoal%20seamless%20studio%20background%2C%20cool%20moody%20editorial%20lighting%20with%20a%20single%20soft%20key%20light%20from%20upper%20left%20creating%20gentle%20rim%20highlights%20on%20the%20bead%20blasted%20metal%20surface%2C%20subtle%20cyan%20blue%20fill%20light%20contrasting%20with%20warm%20sand%20tones%2C%20shot%20on%20Hasselblad%20medium%20format%20with%2080mm%20lens%20at%20f4%20ISO%20100%2C%20visible%20microtexture%20of%20bead%20blasted%20aluminum%20grain%20and%20fine%20dust%20particles%20catching%20light%2C%20accurate%20soft%20contact%20shadow%2C%20shallow%20depth%20of%20field%2C%20cinematic%20dark%20mood%2C%20lifelike%20realism%2C%20centered%20subject%2C%20luxury%20audio%20product%20photography&width=900&height=1200&seq=axiom-colorway-sand-v5-dark&orientation=portrait',
  },
];

const AUTO_CYCLE_MS = 6000;

type Ripple = { id: number; x: number; y: number };

export default function Colorways() {
  const sectionRef = useRef<HTMLElement>(null);
  const imageWrapRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [tick, setTick] = useState(0);
  const [paused, setPaused] = useState(false);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0, px: 50, py: 50 });
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const rippleIdRef = useRef(0);

  const active = COLORWAYS[activeIdx];

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setInView(true);
        });
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Auto-cycle progress + rotation
  useEffect(() => {
    if (!inView || paused) return;
    const start = performance.now();
    let raf = 0;
    const step = (t: number) => {
      const elapsed = (t - start) % AUTO_CYCLE_MS;
      setTick(elapsed / AUTO_CYCLE_MS);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);

    const rotator = window.setInterval(() => {
      setActiveIdx((i) => (i + 1) % COLORWAYS.length);
    }, AUTO_CYCLE_MS);

    return () => {
      cancelAnimationFrame(raf);
      window.clearInterval(rotator);
    };
  }, [inView, paused, activeIdx]);

  // Keyboard arrow switching (when section visible)
  useEffect(() => {
    if (!inView) return;
    const onKey = (e: KeyboardEvent) => {
      // don't hijack keys while typing in an input elsewhere
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowRight') {
        setActiveIdx((i) => (i + 1) % COLORWAYS.length);
      } else if (e.key === 'ArrowLeft') {
        setActiveIdx((i) => (i - 1 + COLORWAYS.length) % COLORWAYS.length);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [inView]);

  const fadeUp = (delay: number, distance = 18) => ({
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0)' : `translateY(${distance}px)`,
    transition: `opacity 900ms ease ${delay}ms, transform 900ms cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
  });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = imageWrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const nx = x * 2 - 1;
    const ny = y * 2 - 1;
    setTilt({
      rx: -ny * 4.5,
      ry: nx * 6,
      px: x * 100,
      py: y * 100,
    });
  };
  const handleMouseLeave = () => {
    setTilt({ rx: 0, ry: 0, px: 50, py: 50 });
  };

  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = imageWrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const id = ++rippleIdRef.current;
    setRipples((prev) => [...prev, { id, x, y }]);
    window.setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== id));
    }, 900);
    // Advance finish
    setActiveIdx((i) => (i + 1) % COLORWAYS.length);
  };

  return (
    <section
      ref={sectionRef}
      id="colorways"
      className="relative w-full"
      style={{
        backgroundColor: '#141B2A',
        paddingTop: '120px',
        paddingBottom: '120px',
        fontFamily:
          'Inter, "Helvetica Neue", Helvetica, Arial, system-ui, sans-serif',
        overflow: 'hidden',
        transition: 'background-color 700ms ease',
      }}
    >
      {/* Ambient glow */}
      <div
        aria-hidden="true"
        className="absolute pointer-events-none"
        style={{
          top: '10%',
          right: '-15%',
          width: '60vw',
          height: '60vw',
          background: `radial-gradient(circle at 50% 50%, ${active.glow} 0%, rgba(0,0,0,0) 60%)`,
          transition: 'background 900ms ease',
          filter: 'blur(20px)',
        }}
      />

      <div
        className="absolute left-0 right-0 top-0"
        style={{
          height: '1px',
          background:
            'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.14) 20%, rgba(255,255,255,0.14) 80%, rgba(255,255,255,0) 100%)',
        }}
      />

      <div
        className="relative mx-auto"
        style={{ maxWidth: '1280px', paddingLeft: '56px', paddingRight: '56px' }}
      >
        {/* Header */}
        <div className="grid grid-cols-12 gap-8 items-end mb-14">
          <div className="col-span-12 lg:col-span-8">
            <div
              className="uppercase"
              style={{
                color: '#7AA8EE',
                fontSize: '11px',
                letterSpacing: '0.32em',
                fontWeight: 500,
                marginBottom: '20px',
                ...fadeUp(80, 10),
              }}
            >
              004 — COLORWAYS
            </div>
            <h2
              style={{
                color: '#FFFFFF',
                fontSize: '76px',
                fontWeight: 700,
                lineHeight: 0.98,
                letterSpacing: '-0.03em',
                margin: 0,
                ...fadeUp(200, 22),
              }}
            >
              Three finishes.
              <br />
              <span
                style={{
                  fontStyle: 'italic',
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.72)',
                }}
              >
                One tuning.
              </span>
            </h2>
          </div>

          <div className="col-span-12 lg:col-span-4">
            <p
              style={{
                color: '#9AA0AC',
                fontSize: '15px',
                lineHeight: 1.8,
                margin: 0,
                paddingBottom: '10px',
                ...fadeUp(320, 14),
              }}
            >
              Same curve. Different surface.
            </p>
          </div>
        </div>

        {/* Split */}
        <div className="grid grid-cols-12 gap-10 items-start">
          {/* Image */}
          <div
            className="col-span-12 lg:col-span-7"
            style={{ perspective: '1400px' }}
          >
            <div
              ref={imageWrapRef}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onMouseEnter={() => setPaused(true)}
              onClick={handleImageClick}
              onFocus={() => setPaused(true)}
              onBlur={() => setPaused(false)}
              className="relative overflow-hidden cursor-pointer"
              style={{
                height: '560px',
                background:
                  'linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.15) 100%)',
                transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`,
                transformStyle: 'preserve-3d',
                transition: 'transform 380ms cubic-bezier(0.22,1,0.36,1)',
                willChange: 'transform',
                ...fadeUp(300, 22),
              }}
            >
              {COLORWAYS.map((c, ci) => (
                <img
                  key={c.id}
                  src={c.image}
                  alt={`AX-07 ${c.name}`}
                  className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none"
                  draggable={false}
                  style={{
                    opacity: ci === activeIdx ? 1 : 0,
                    transform:
                      ci === activeIdx
                        ? `scale(1.06) translate(${(tilt.px - 50) * -0.08}px, ${(tilt.py - 50) * -0.08}px)`
                        : 'scale(1.1)',
                    transition:
                      'opacity 700ms cubic-bezier(0.22,1,0.36,1), transform 900ms cubic-bezier(0.22,1,0.36,1)',
                  }}
                />
              ))}

              {/* Cursor sheen */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: `radial-gradient(circle at ${tilt.px}% ${tilt.py}%, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0) 45%)`,
                  mixBlendMode: 'overlay',
                  transition: 'background 200ms ease-out',
                }}
              />

              {/* Bottom gradient scrim keeps caption readable */}
              <div
                className="absolute inset-x-0 bottom-0 pointer-events-none"
                style={{
                  height: '46%',
                  background:
                    'linear-gradient(180deg, rgba(20,27,42,0) 0%, rgba(20,27,42,0.35) 55%, rgba(20,27,42,0.75) 100%)',
                }}
              />

              {/* Ripples */}
              {ripples.map((r) => (
                <span
                  key={r.id}
                  className="axiom-ripple"
                  style={{
                    left: `${r.x}px`,
                    top: `${r.y}px`,
                  }}
                />
              ))}

              {/* Overlay: index */}
              <div
                className="absolute uppercase"
                style={{
                  left: '24px',
                  top: '22px',
                  color: 'rgba(255,255,255,0.72)',
                  fontSize: '11px',
                  letterSpacing: '0.32em',
                  fontWeight: 500,
                }}
              >
                {active.code}
              </div>

              {/* Overlay: caption */}
              <div
                className="absolute"
                style={{ left: '28px', bottom: '28px', maxWidth: '70%' }}
              >
                <div
                  className="uppercase"
                  style={{
                    color: 'rgba(255,255,255,0.55)',
                    fontSize: '10px',
                    letterSpacing: '0.32em',
                    fontWeight: 500,
                    marginBottom: '8px',
                  }}
                >
                  AX-07
                </div>
                <div
                  key={`${active.id}-caption`}
                  className="axiom-slide-in"
                  style={{
                    color: '#FFFFFF',
                    fontSize: '30px',
                    fontWeight: 700,
                    letterSpacing: '-0.015em',
                    lineHeight: 1.1,
                  }}
                >
                  {active.name}
                </div>
              </div>

              {/* Hint */}
              <div
                className="absolute uppercase flex items-center gap-2"
                style={{
                  right: '20px',
                  bottom: '20px',
                  color: 'rgba(255,255,255,0.85)',
                  fontSize: '9.5px',
                  letterSpacing: '0.28em',
                  fontWeight: 500,
                }}
              >
                <span
                  className="inline-block"
                  style={{
                    width: '5px',
                    height: '5px',
                    background: '#7AA8EE',
                    borderRadius: '50%',
                  }}
                />
                CLICK
              </div>
            </div>

            {/* keyboard hint below */}
            <div
              className="flex items-center gap-3 mt-4"
              style={{
                color: 'rgba(255,255,255,0.42)',
                fontSize: '10.5px',
                letterSpacing: '0.28em',
                fontWeight: 500,
                textTransform: 'uppercase',
              }}
            >
              <KeyBadge label="←" />
              <KeyBadge label="→" />
            </div>
          </div>

          {/* Meta */}
          <div className="col-span-12 lg:col-span-5 lg:pt-4">
            <div
              className="uppercase"
              style={{
                color: 'rgba(255,255,255,0.42)',
                fontSize: '10px',
                letterSpacing: '0.32em',
                fontWeight: 500,
                marginBottom: '12px',
                ...fadeUp(360, 12),
              }}
            >
              SELECTED
            </div>

            <h3
              key={`${active.id}-h`}
              className="axiom-fade-swap"
              style={{
                color: '#FFFFFF',
                fontSize: '44px',
                fontWeight: 700,
                lineHeight: 1.05,
                letterSpacing: '-0.02em',
                margin: 0,
                marginBottom: '10px',
                ...fadeUp(420, 14),
              }}
            >
              {active.name}
            </h3>
            <p
              key={`${active.id}-s`}
              className="axiom-fade-swap"
              style={{
                color: '#9AA0AC',
                fontSize: '17px',
                lineHeight: 1.6,
                fontWeight: 400,
                margin: 0,
                marginBottom: '40px',
                letterSpacing: '0.005em',
                ...fadeUp(480, 14),
              }}
            >
              {active.subtitle}
            </p>

            <div style={fadeUp(540, 14)}>
              {[
                { label: 'FINISH', value: active.finish },
                { label: 'HEX', value: active.hex.toUpperCase() },
                { label: 'EDITION', value: active.edition },
                { label: 'SHIPS', value: 'Q3 · 2026' },
              ].map((row, ri) => (
                <div
                  key={row.label}
                  className="grid grid-cols-[130px_1fr] gap-4 items-baseline"
                  style={{
                    paddingTop: '18px',
                    paddingBottom: '18px',
                    borderTop:
                      ri === 0
                        ? '1px solid rgba(255,255,255,0.14)'
                        : '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <span
                    className="uppercase"
                    style={{
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: '10px',
                      letterSpacing: '0.28em',
                      fontWeight: 500,
                    }}
                  >
                    {row.label}
                  </span>
                  <span
                    key={`${active.id}-${row.label}`}
                    className="axiom-fade-swap"
                    style={{
                      color: '#FFFFFF',
                      fontSize: '15px',
                      fontWeight: 500,
                      letterSpacing: '0.005em',
                    }}
                  >
                    {row.value}
                  </span>
                </div>
              ))}
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.14)' }} />
            </div>

            <a
              href="#reserve"
              className="inline-flex items-center gap-2 whitespace-nowrap cursor-pointer mt-10 axiom-link-arrow"
              style={{
                color: '#7AA8EE',
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                textDecoration: 'none',
                paddingBottom: '4px',
                borderBottom: '1px solid rgba(122,168,238,0.5)',
                ...fadeUp(620, 14),
              }}
            >
              <span>Reserve</span>
              <i className="ri-arrow-right-line" style={{ fontSize: '15px' }} />
            </a>
          </div>
        </div>

        {/* Swatch selector */}
        <div
          className="grid grid-cols-3 gap-0 mt-16"
          style={{
            borderTop: '1px solid rgba(255,255,255,0.14)',
            ...fadeUp(700, 18),
          }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {COLORWAYS.map((c, idx) => {
            const isActive = idx === activeIdx;
            const progress = isActive ? tick : 0;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setActiveIdx(idx)}
                onMouseEnter={() => setActiveIdx(idx)}
                className="group text-left whitespace-nowrap cursor-pointer relative"
                style={{
                  padding: '32px 4px 28px 4px',
                  background: 'transparent',
                  border: 'none',
                  borderRight:
                    idx < COLORWAYS.length - 1
                      ? '1px solid rgba(255,255,255,0.08)'
                      : undefined,
                  transition: 'transform 480ms cubic-bezier(0.22,1,0.36,1)',
                }}
              >
                <span
                  className="absolute left-0 right-0 top-0"
                  style={{
                    height: '2px',
                    background: '#7AA8EE',
                    transform: isActive ? 'scaleX(1)' : 'scaleX(0)',
                    transformOrigin: 'left center',
                    transition:
                      'transform 480ms cubic-bezier(0.22,1,0.36,1)',
                  }}
                />

                <div className="flex items-center gap-5">
                  <span
                    className="relative flex-shrink-0"
                    style={{
                      width: '62px',
                      height: '62px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition:
                        'transform 480ms cubic-bezier(0.22,1,0.36,1)',
                    }}
                  >
                    <span
                      className="absolute inset-0 rounded-full pointer-events-none"
                      style={{
                        background: isActive
                          ? `conic-gradient(#7AA8EE ${progress * 360}deg, rgba(255,255,255,0.08) ${progress * 360}deg)`
                          : 'transparent',
                        transition: paused ? 'background 200ms ease' : 'none',
                      }}
                    />
                    {/* pulsing halo when active */}
                    {isActive && (
                      <span
                        className="absolute inset-0 rounded-full pointer-events-none axiom-swatch-halo"
                        style={{
                          background: `radial-gradient(circle, ${c.glow} 0%, rgba(0,0,0,0) 70%)`,
                        }}
                      />
                    )}
                    <span
                      className="block relative"
                      style={{
                        width: '54px',
                        height: '54px',
                        borderRadius: '50%',
                        backgroundColor: c.hex,
                        border: `1px solid ${
                          isActive
                            ? 'rgba(122,168,238,0.5)'
                            : 'rgba(255,255,255,0.14)'
                        }`,
                        transition:
                          'border-color 300ms ease, transform 400ms cubic-bezier(0.22,1,0.36,1), box-shadow 400ms ease',
                        transform: isActive ? 'scale(1.02)' : 'scale(0.94)',
                        boxShadow: isActive
                          ? `0 8px 26px -6px ${c.glow}, inset 0 1px 2px rgba(255,255,255,0.08)`
                          : 'inset 0 1px 2px rgba(255,255,255,0.04)',
                      }}
                    />
                  </span>
                  <div>
                    <div
                      className="uppercase"
                      style={{
                        color: 'rgba(255,255,255,0.42)',
                        fontSize: '10px',
                        letterSpacing: '0.32em',
                        fontWeight: 500,
                        marginBottom: '6px',
                      }}
                    >
                      {c.code}
                    </div>
                    <div
                      style={{
                        color: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.72)',
                        fontSize: '20px',
                        fontWeight: 700,
                        letterSpacing: '-0.005em',
                        lineHeight: 1.2,
                        marginBottom: '4px',
                        transition: 'color 220ms ease, transform 320ms cubic-bezier(0.22,1,0.36,1)',
                        transform: isActive ? 'translateX(2px)' : 'translateX(0)',
                      }}
                    >
                      {c.name}
                    </div>
                    <div
                      style={{
                        color: 'rgba(255,255,255,0.5)',
                        fontSize: '13px',
                        fontWeight: 400,
                        letterSpacing: '0.005em',
                      }}
                    >
                      {c.finish}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

      </div>
    </section>
  );
}

function KeyBadge({ label }: { label: string }) {
  return (
    <span
      className="inline-flex items-center justify-center"
      style={{
        minWidth: '26px',
        height: '24px',
        padding: '0 6px',
        border: '1px solid rgba(255,255,255,0.22)',
        borderRadius: '4px',
        color: 'rgba(255,255,255,0.72)',
        fontSize: '12px',
        fontWeight: 600,
        letterSpacing: '0',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      }}
    >
      {label}
    </span>
  );
}